import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { HttpOrder } from "../http";

const isGuid = (value: string) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

const MercadoPagoFailureReturnPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const sp = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const orderGuid = sp.get("o") || "";
    const txGuid = sp.get("t") || sp.get("external_reference") || "";

    const [error, setError] = useState<string | null>(null);
    const didRunRef = useRef(false);

    const missingParams = !orderGuid || !txGuid;

    const MP_FAILURE_RESUME_GUID_KEY = "mp_failure_resume_guid";

    useEffect(() => {
        if (didRunRef.current) return;
        didRunRef.current = true;

        const run = async () => {
            if (missingParams) {
                setError(
                    "Faltan parámetros para recuperar tu compra. Volvé a intentar desde la tienda.",
                );
                return;
            }
            if (!isGuid(orderGuid)) {
                setError("El identificador de orden no es válido.");
                return;
            }

            try {
                await HttpOrder.revertOrder(orderGuid);

                sessionStorage.setItem(MP_FAILURE_RESUME_GUID_KEY, orderGuid);

                navigate(`/checkout-reanudar-guid/${orderGuid}?t=${encodeURIComponent(txGuid)}`, {
                    replace: true,
                });
            } catch (e: any) {
                setError("No se pudo restaurar tu carrito. Volvé a intentar desde la tienda.");
            }
        };

        void run();
    }, [missingParams, navigate, orderGuid, txGuid]);

    if (error) {
        return (
            <Box sx={{ maxWidth: 800, mx: "auto", mt: 6, px: 2 }}>
                <Stack spacing={2} alignItems="center" textAlign="center">
                    <Typography fontFamily="inherit" fontSize="18px" fontWeight={600}>
                        No pudimos recuperar tu compra
                    </Typography>
                    <Typography fontFamily="inherit" fontSize="14px" sx={{ opacity: 0.8 }}>
                        {error}
                    </Typography>
                    <Button variant="contained" onClick={() => navigate("/")}>
                        Volver a la tienda
                    </Button>
                </Stack>
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 800, mx: "auto", mt: 6, px: 2 }}>
            <Stack spacing={2} alignItems="center" textAlign="center">
                <CircularProgress size={22} />
                <Typography fontFamily="inherit" fontSize="16px" fontWeight={600}>
                    Restaurando tu carrito
                </Typography>
                <Typography fontFamily="inherit" fontSize="13px" sx={{ opacity: 0.75 }}>
                    Te vamos a devolver al checkout para que puedas reintentar el pago.
                </Typography>
            </Stack>
        </Box>
    );
};

export default MercadoPagoFailureReturnPage;
